/**
 * Catálogo de permisos de la aplicación.
 *
 * ESTA ES LA ÚNICA FUENTE DE VERDAD sobre qué permisos existen. Firestore
 * únicamente guarda strings; este archivo decide cuáles son válidos y aporta
 * las etiquetas que ve el administrador.
 *
 * Un permiso solo existe si hay código que lo respeta, por eso el catálogo es
 * código. El mapeo rol -> permisos vive en Firestore (`rbac_roles`) y se edita
 * desde la UI sin desplegar.
 *
 * ⚠️ SEGURIDAD: el gating basado en este catálogo es organización y UX, NO
 * seguridad. El navegador habla directo a Firestore con las credenciales del
 * propio usuario, así que cualquiera puede leer y escribir lo que las reglas
 * permitan sin importar qué devuelva `can()`. La aplicación real del permiso
 * debe duplicarse en `firestore.rules` y en la API REST. Ver `docs/RBAC.md`.
 */

/** Un módulo agrupa permisos de una misma área funcional. */
export interface PermissionDef {
  /** Identificador estable `module:action`. Nunca se traduce. */
  readonly id: string;
  /** Etiqueta corta en español, mostrada en el editor de roles. */
  readonly label: string;
  /** Explicación de qué habilita el permiso. */
  readonly description: string;
}

export interface PermissionModuleDef {
  /** Identificador del módulo, primer segmento de los ids de sus permisos. */
  readonly id: string;
  /** Nombre del módulo en español. */
  readonly label: string;
  /** Explicación del área que cubre el módulo. */
  readonly description: string;
  readonly permissions: readonly PermissionDef[];
}

export const PERMISSION_MODULES = [
  {
    id: "dashboard",
    label: "Panel principal",
    description: "Acceso al hub de métricas y sus vistas.",
    permissions: [
      {
        id: "dashboard:view",
        label: "Ver el panel",
        description: "Acceder a /dashboard y ver el hub de métricas.",
      },
      {
        id: "dashboard:visor_view",
        label: "Ver métricas del visor",
        description: "Acceder a las métricas del visor DICOM en /dashboard/visor.",
      },
    ],
  },
  {
    id: "users",
    label: "Usuarios",
    description: "Gestión de las cuentas de la plataforma médica.",
    permissions: [
      {
        id: "users:list",
        label: "Listar usuarios",
        description: "Acceder a /users y ver el listado con sus filtros.",
      },
      {
        id: "users:view",
        label: "Ver detalle de usuario",
        description: "Abrir la ficha de un usuario y consultar sus datos.",
      },
      {
        id: "users:create",
        label: "Crear usuarios",
        description: "Dar de alta usuarios nuevos, incluida su cuenta de acceso.",
      },
      {
        id: "users:edit",
        label: "Editar usuarios",
        description: "Modificar los datos de perfil de un usuario existente.",
      },
      {
        id: "users:delete",
        label: "Eliminar usuarios",
        description: "Borrar definitivamente la cuenta de un usuario.",
      },
      {
        id: "users:toggle_active",
        label: "Activar o desactivar usuarios",
        description: "Habilitar o bloquear el acceso de una cuenta.",
      },
      {
        id: "users:reset_password_email",
        label: "Enviar restablecimiento de contraseña",
        description: "Enviar al usuario el correo para que cambie su contraseña.",
      },
      {
        id: "users:set_password",
        label: "Asignar contraseña directamente",
        description:
          "Establecer la contraseña de un usuario sin su intervención. Permiso sensible.",
      },
      {
        id: "users:verify_email",
        label: "Verificar correo",
        description: "Marcar un correo como verificado sin confirmación del usuario.",
      },
      {
        id: "users:resend_verification",
        label: "Reenviar verificación",
        description: "Volver a enviar el correo de verificación de la cuenta.",
      },
      {
        id: "users:view_stats",
        label: "Ver estadísticas de usuarios",
        description: "Consultar los indicadores agregados del listado de usuarios.",
      },
    ],
  },
  {
    id: "roles",
    label: "Roles y permisos",
    description: "Administración del propio sistema de permisos.",
    permissions: [
      {
        id: "roles:view",
        label: "Ver roles",
        description: "Consultar los roles existentes y sus permisos.",
      },
      {
        id: "roles:create",
        label: "Crear roles",
        description: "Definir roles nuevos con su conjunto de permisos.",
      },
      {
        id: "roles:edit",
        label: "Editar roles",
        description:
          "Modificar el nombre, la descripción o los permisos de un rol. Permiso sensible.",
      },
      {
        id: "roles:delete",
        label: "Eliminar roles",
        description: "Borrar roles personalizados. Los roles del sistema no se pueden borrar.",
      },
      {
        id: "roles:assign",
        label: "Asignar roles",
        description: "Cambiar los roles que tiene asignados una persona. Permiso sensible.",
      },
    ],
  },
  {
    id: "api_keys",
    label: "Llaves de API",
    description: "Credenciales de acceso máquina a máquina de cada usuario.",
    permissions: [
      {
        id: "api_keys:list",
        label: "Listar llaves de API",
        description: "Ver las llaves de API asociadas a un usuario.",
      },
      {
        id: "api_keys:create",
        label: "Crear llaves de API",
        description: "Generar una llave nueva y ver su secreto una única vez.",
      },
      {
        id: "api_keys:edit",
        label: "Editar llaves de API",
        description: "Cambiar el nombre o los endpoints autorizados de una llave.",
      },
      {
        id: "api_keys:regenerate",
        label: "Regenerar llaves de API",
        description: "Emitir un secreto nuevo e invalidar el anterior.",
      },
      {
        id: "api_keys:delete",
        label: "Eliminar llaves de API",
        description: "Revocar definitivamente una llave.",
      },
    ],
  },
  {
    id: "api_catalog",
    label: "Catálogo de API",
    description: "Endpoints y grupos de permisos que se pueden conceder a una llave.",
    permissions: [
      {
        id: "api_catalog:view",
        label: "Ver el catálogo de API",
        description: "Consultar los endpoints y grupos configurados.",
      },
      {
        id: "api_catalog:edit",
        label: "Editar endpoints",
        description: "Dar de alta, modificar o borrar endpoints del catálogo.",
      },
      {
        id: "api_catalog:groups_edit",
        label: "Editar grupos de permisos",
        description: "Administrar los grupos que agrupan endpoints de la API.",
      },
    ],
  },
  {
    id: "imagings",
    label: "Estudios de imagen",
    description: "Estudios DICOM, informes médicos y su distribución.",
    permissions: [
      {
        id: "imagings:list",
        label: "Listar estudios",
        description: "Ver el listado de estudios y aplicar filtros.",
      },
      {
        id: "imagings:view",
        label: "Ver detalle del estudio",
        description: "Abrir la ficha de un estudio y su información.",
      },
      {
        id: "imagings:viewer_open",
        label: "Abrir el visor DICOM",
        description: "Generar un token temporal y abrir el estudio en el visor.",
      },
      {
        id: "imagings:share",
        label: "Compartir estudios",
        description: "Emitir enlaces externos y notificaciones de un estudio.",
      },
      {
        id: "imagings:download",
        label: "Descargar estudios",
        description: "Solicitar y descargar los archivos del estudio.",
      },
      {
        id: "imagings:export_excel",
        label: "Exportar estudios a Excel",
        description: "Generar y descargar el listado de estudios en Excel.",
      },
      {
        id: "imagings:delete_study",
        label: "Eliminar estudios",
        description: "Borrar un estudio del sistema. Permiso sensible.",
      },
      {
        id: "imagings:reports_view",
        label: "Ver informes médicos",
        description: "Consultar los informes asociados a un estudio.",
      },
      {
        id: "imagings:reports_upload",
        label: "Subir informes médicos",
        description: "Adjuntar informes manuales a un estudio.",
      },
      {
        id: "imagings:reports_delete",
        label: "Eliminar informes médicos",
        description: "Borrar informes manuales o de RIS de un estudio.",
      },
      {
        id: "imagings:shared_audit",
        label: "Ver auditoría de compartidos",
        description: "Consultar el historial de accesos a estudios compartidos.",
      },
    ],
  },
  {
    id: "licenses",
    label: "Licencias",
    description: "Emisión de licencias y aprobación de solicitudes.",
    permissions: [
      {
        id: "licenses:view",
        label: "Ver licencias",
        description: "Acceder a /licenses y consultar licencias y solicitudes.",
      },
      {
        id: "licenses:generate",
        label: "Generar licencias",
        description: "Emitir una licencia nueva.",
      },
      {
        id: "licenses:verify",
        label: "Verificar licencias",
        description: "Comprobar la validez de una licencia existente.",
      },
      {
        id: "licenses:delete",
        label: "Eliminar licencias",
        description: "Borrar una licencia emitida.",
      },
      {
        id: "licenses:change_status",
        label: "Cambiar estado de licencias",
        description: "Activar, suspender o revocar una licencia.",
      },
      {
        id: "licenses:approve_request",
        label: "Aprobar solicitudes",
        description: "Aprobar una solicitud de licencia pendiente.",
      },
      {
        id: "licenses:reject_request",
        label: "Rechazar solicitudes",
        description: "Rechazar una solicitud de licencia pendiente.",
      },
    ],
  },
  {
    id: "servers",
    label: "Infraestructura de centros",
    description: "Fichas de centro, hosts, software instalado e inventario.",
    permissions: [
      {
        id: "servers:view",
        label: "Ver infraestructura",
        description: "Acceder a /servers y consultar la ficha de un centro.",
      },
      {
        id: "servers:infra_edit",
        label: "Editar la ficha del centro",
        description: "Crear o modificar los datos de infraestructura de un centro.",
      },
      {
        id: "servers:hosts_manage",
        label: "Gestionar hosts",
        description: "Crear, editar y eliminar hosts de un centro.",
      },
      {
        id: "servers:software_manage",
        label: "Gestionar software instalado",
        description: "Crear, editar y eliminar instancias de software de un centro.",
      },
      {
        id: "servers:software_catalog_manage",
        label: "Gestionar el catálogo de software",
        description: "Administrar los tipos de software disponibles.",
      },
      {
        id: "servers:report_view",
        label: "Ver el inventario de software",
        description: "Acceder al reporte consolidado de inventario.",
      },
      {
        id: "servers:report_export",
        label: "Exportar el inventario",
        description: "Descargar el inventario de software y hosts en CSV.",
      },
      {
        id: "servers:settings_edit",
        label: "Editar política de infraestructura",
        description:
          "Modificar la política de soporte de sistemas operativos, versiones objetivo e intervalo de revisión.",
      },
    ],
  },
  {
    id: "settings",
    label: "Configuración de plataforma",
    description: "Ajustes globales, plantillas médicas y planes.",
    permissions: [
      {
        id: "settings:view",
        label: "Ver la configuración",
        description: "Acceder a /settings/platform.",
      },
      {
        id: "settings:infra_edit",
        label: "Editar ajustes de infraestructura",
        description: "Modificar los ajustes globales de infraestructura.",
      },
      {
        id: "settings:banner_edit",
        label: "Editar el aviso de plataforma",
        description: "Publicar o retirar la notificación global de la plataforma.",
      },
      {
        id: "settings:email_blacklist_edit",
        label: "Editar la lista negra de correos",
        description: "Administrar los dominios y correos bloqueados.",
      },
      {
        id: "settings:revision_interval_edit",
        label: "Editar el intervalo de revisión",
        description: "Cambiar cada cuánto se revisa la infraestructura.",
      },
      {
        id: "settings:api_endpoints_edit",
        label: "Editar el catálogo de endpoints",
        description: "Administrar los endpoints publicados de la API.",
      },
      {
        id: "settings:templates_manage",
        label: "Gestionar plantillas médicas",
        description: "Crear, editar y eliminar plantillas de informes médicos.",
      },
      {
        id: "settings:plans_manage",
        label: "Gestionar planes",
        description: "Crear, editar y eliminar los planes de suscripción.",
      },
    ],
  },
  {
    id: "dns",
    label: "DNS",
    description: "Registros DNS de las zonas configuradas en Cloudflare.",
    permissions: [
      {
        id: "dns:list",
        label: "Listar registros DNS",
        description: "Acceder a /dns y consultar zonas y registros.",
      },
      {
        id: "dns:create",
        label: "Crear registros DNS",
        description: "Dar de alta un registro nuevo en una zona.",
      },
      {
        id: "dns:edit",
        label: "Editar registros DNS",
        description: "Modificar un registro existente.",
      },
      {
        id: "dns:delete",
        label: "Eliminar registros DNS",
        description: "Borrar un registro de una zona. Permiso sensible.",
      },
    ],
  },
  {
    id: "installations",
    label: "Instalaciones",
    description: "Paquetes de instalación, enlaces de despliegue y sus credenciales.",
    permissions: [
      {
        id: "installations:browse",
        label: "Explorar archivos de instalación",
        description: "Ver los archivos y carpetas de instalación disponibles.",
      },
      {
        id: "installations:download_file",
        label: "Descargar archivos de instalación",
        description: "Descargar carpetas y archivos sueltos del repositorio de instalación.",
      },
      {
        id: "installations:download_package",
        label: "Descargar paquete preconfigurado",
        description: "Generar y descargar el instalador preconfigurado de un centro.",
      },
      {
        id: "installations:generate_link",
        label: "Generar enlaces de instalación",
        description:
          "Emitir un enlace público de instalación. El enlace no requiere autenticación.",
      },
      {
        id: "installations:generate_api_key",
        label: "Generar llave de API de instalación",
        description:
          "Emitir la llave de API que acompaña a una instalación. Permiso sensible.",
      },
    ],
  },
  {
    id: "reports",
    label: "Reportes de negocio",
    description: "Métricas de negocio y su exportación.",
    permissions: [
      {
        id: "reports:business_view",
        label: "Ver reportes de negocio",
        description: "Acceder a /reports/business y consultar sus métricas.",
      },
      {
        id: "reports:business_export",
        label: "Exportar reportes de negocio",
        description: "Descargar el reporte de negocio en Excel.",
      },
    ],
  },
  {
    id: "laboratory",
    label: "Laboratorio",
    description: "Módulo de laboratorio.",
    permissions: [
      {
        id: "laboratory:view",
        label: "Ver laboratorio",
        description: "Acceder a /laboratory.",
      },
    ],
  },
  {
    id: "bioanalysis",
    label: "Bioanálisis",
    description: "Módulo de bioanálisis.",
    permissions: [
      {
        id: "bioanalysis:view",
        label: "Ver bioanálisis",
        description: "Acceder a /bioanalysis y al panel simplificado de bioanalista.",
      },
    ],
  },
] as const satisfies readonly PermissionModuleDef[];

type PermissionModules = typeof PERMISSION_MODULES;

/** Identificador de módulo, p. ej. `"users"`. */
export type PermissionModuleId = PermissionModules[number]["id"];

/** Identificador exacto de permiso, p. ej. `"users:delete"`. */
export type PermissionId = PermissionModules[number]["permissions"][number]["id"];

/** Comodín que concede todos los permisos de un módulo, p. ej. `"users:*"`. */
export type ModuleWildcard = `${PermissionModuleId}:*`;

/** Comodín que concede absolutamente todo. */
export const GLOBAL_WILDCARD = "*" as const;

/**
 * Lo que se puede almacenar en `rbac_roles.permissions`: un permiso exacto,
 * un comodín de módulo, o el comodín global.
 */
export type PermissionOrWildcard = PermissionId | ModuleWildcard | typeof GLOBAL_WILDCARD;

/** Todos los ids de permiso del catálogo. */
export const ALL_PERMISSION_IDS: readonly PermissionId[] = PERMISSION_MODULES.flatMap(
  (module) => module.permissions.map((permission) => permission.id)
);

/** Todos los ids de módulo del catálogo. */
export const ALL_MODULE_IDS: readonly PermissionModuleId[] = PERMISSION_MODULES.map(
  (module) => module.id
);

const PERMISSION_ID_SET: ReadonlySet<string> = new Set(ALL_PERMISSION_IDS);
const MODULE_ID_SET: ReadonlySet<string> = new Set(ALL_MODULE_IDS);

/** Un permiso del catálogo junto con el módulo al que pertenece. */
export type PermissionEntry = PermissionDef & { readonly moduleId: PermissionModuleId };

/** Índice por id, para resolver etiquetas sin recorrer el catálogo. */
export const PERMISSION_BY_ID: Readonly<Record<string, PermissionEntry>> =
  Object.fromEntries(
    PERMISSION_MODULES.flatMap((module) =>
      module.permissions.map((permission) => [
        permission.id,
        { ...permission, moduleId: module.id },
      ])
    )
  );

/** ¿El string es un permiso exacto del catálogo? */
export function isPermissionId(value: string): value is PermissionId {
  return PERMISSION_ID_SET.has(value);
}

/** ¿El string es un id de módulo del catálogo? */
export function isModuleId(value: string): value is PermissionModuleId {
  return MODULE_ID_SET.has(value);
}

/**
 * ¿El string es almacenable en `rbac_roles.permissions`?
 *
 * Acepta el comodín global, un comodín de módulo cuyo módulo exista, o un
 * permiso exacto del catálogo. Cualquier otra cosa es un valor de una versión
 * futura o un error tipográfico, y se descarta al leer.
 */
export function isPermissionOrWildcard(value: string): value is PermissionOrWildcard {
  if (value === GLOBAL_WILDCARD) return true;
  if (value.endsWith(":*")) return isModuleId(value.slice(0, -2));
  return isPermissionId(value);
}

/** Todos los permisos de un módulo, para expandir `module:*` en la UI. */
export function permissionsOfModule(moduleId: string): readonly PermissionId[] {
  const module = PERMISSION_MODULES.find((candidate) => candidate.id === moduleId);
  return module ? module.permissions.map((permission) => permission.id) : [];
}
